const STORAGE_KEY = "__fsp_chunk_reload_attempts__";

const CHUNK_ERROR_PATTERNS = [
  "ChunkLoadError",
  "Loading chunk",
  "Failed to fetch dynamically imported module",
  "Importing a module script failed",
];

const getErrorMessage = (error) =>
  (typeof error === "string" && error) || error?.message || error?.name || "";

export const isChunkError = (error) => {
  const message = getErrorMessage(error);
  return (
    typeof message === "string" &&
    CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern))
  );
};

export const getChunkErrorDetails = (error) => {
  const message = getErrorMessage(error);
  const urlMatch =
    typeof message === "string"
      ? message.match(/https?:\/\/[^\s)]+(?:\.chunk)?\.js[^\s)]*/i)
      : null;
  const chunkMatch =
    typeof message === "string" ? message.match(/(?:chunk\s+)(\d+)/i) : null;

  return {
    message: typeof message === "string" ? message : "Unknown chunk load error",
    chunkId: chunkMatch?.[1] || null,
    chunkUrl: urlMatch?.[0] || null,
  };
};

const getFingerprint = (error) => {
  const { chunkId, chunkUrl, message } = getChunkErrorDetails(error);
  return [chunkId || "unknown", chunkUrl || message].join("|");
};

const safeGetAttempts = () => {
  try {
    const attempts = JSON.parse(sessionStorage.getItem(STORAGE_KEY) || "{}");
    return attempts && typeof attempts === "object" ? attempts : {};
  } catch (error) {
    return {};
  }
};

const safeSetAttempt = (fingerprint) => {
  try {
    const attempts = safeGetAttempts();
    attempts[fingerprint] = Date.now();
    sessionStorage.setItem(STORAGE_KEY, JSON.stringify(attempts));
  } catch (error) {
    // Recovery still works when storage is unavailable (for example, private mode).
  }
};

export const shouldReloadForChunkError = (error) => {
  if (!isChunkError(error)) return false;
  return !safeGetAttempts()[getFingerprint(error)];
};

const reloadLatestApp = () => {
  if (typeof window === "undefined" || !window.location) return;

  const url = new URL(window.location.href);
  // A unique navigation prevents the browser from reusing an already failed app shell.
  url.searchParams.set("__fsp_update", Date.now().toString());
  window.location.replace(url.toString());
};

export const reloadOnceForChunkError = (error, beforeReload) => {
  if (!shouldReloadForChunkError(error)) return false;

  safeSetAttempt(getFingerprint(error));
  Promise.resolve(beforeReload?.())
    .catch(() => undefined)
    .finally(() => window.setTimeout(reloadLatestApp, 0));

  return true;
};

export default reloadOnceForChunkError;
